import { Cancel1Icon, TickIcon } from "@/domains/icons/huge-icons/monotone"
import { Lozenge } from "@/domains/common/components/lozenge"

import { LastEpochVoteBarView } from "./last-epoch-vote-bar-view"
import type { LockGridCardViewProps } from "./types"

export const LockGridCardView = ({ lock, isSelected, onToggle }: LockGridCardViewProps) => {
  return (
    <div
      onClick={() => onToggle(lock.id)}
      className={`
        group relative flex min-h-[140px] cursor-pointer flex-col justify-between
        rounded-xl border-2 p-4 transition-all duration-200
        ${
          isSelected
            ? "border-primary-400 bg-primary-400/20 shadow-[0_0_20px_rgba(59,130,246,0.15)]"
            : "border-p300-light bg-p300 hover:border-p300-light hover:bg-p300-light"
        }
      `}
    >
      {/* Header: ID & Checkbox */}
      <div className="mb-2 flex items-start justify-between">
        <span className={`text-lg font-bold ${isSelected ? "text-t100" : "text-t200"}`}>#{lock.id}</span>

        {/* Custom Checkbox UI */}
        <div
          className={`
            flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all duration-200
            ${isSelected ? "scale-110 border-primary-400 bg-primary-400" : "border-p300-light group-hover:border-primary-400"}
        `}
        >
          {isSelected && <TickIcon size={14} strokeWidth={3} className="text-t100" />}
        </div>
      </div>

      {/* Body: Power Metric */}
      <div className="mt-auto space-y-3">
        <div>
          <div className="flex items-baseline gap-1">
            <span
              className={`text-2xl font-extrabold ${isSelected ? "text-primary-400" : "text-t200 group-hover:text-t100"}`}
            >
              {lock.lockValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}
            </span>
            <span className="text-xs font-bold uppercase text-t300">veSCALE</span>
          </div>
        </div>

        {/* Last Epoch Vote Bar - Show if there are votes, or show placeholder for debugging */}
        {lock.lastEpochVotes.length > 0 ? (
          <div className="pt-2">
            <LastEpochVoteBarView lastEpochVotes={lock.lastEpochVotes} />
          </div>
        ) : (
          // Debug: Show placeholder to verify bar location (remove after debugging)
          <div className="pt-2 text-xs text-t300 italic">
            No vote history (votes: {lock.lastEpochVotes.length})
          </div>
        )}

        {/* Footer: Meta Data */}
        <div className="flex justify-between items-center border-t border-p300-light pt-3 text-xs font-medium text-t200">
          <div className="flex items-center gap-2">
            <span>{Math.floor(lock.lockAmount)} Locked</span>
            {/* Status Lozenge - Only show for inactive locks (voted or expired) */}
            {lock.hasVotedThisEpoch && (
              <Lozenge color="success" size="xxs" contentLeft={<TickIcon size={12} />}>
                voted
              </Lozenge>
            )}
            {lock.hasExpired && (
              <Lozenge color="error" size="xxs" contentLeft={<Cancel1Icon size={12} />}>
                expired
              </Lozenge>
            )}
          </div>
          <span>{lock.displayLockEnds}</span>
        </div>
      </div>
    </div>
  )
}
